<script setup lang="ts">
import { computed } from 'vue';
import { PermissionSchema, type CanvasAccess, type CanvasAccessKind, type CardUse, type Permission } from '@vp/shared-types';
import { t } from '../i18n';
import Toggle from './Toggle.vue';

/**
 * The permissions a module asks for, as the user grants them (install dialogs, the module page).
 * Canvas access asked as `all` is a choice of three — all objects, only the module's own, none;
 * asked as `own` — on or off; any other permission — on or off.
 */
const props = defineProps<{ requested: readonly Permission[]; granted: readonly Permission[]; disabled?: boolean; cardUses?: readonly CardUse[] }>();
const emit = defineEmits<{ update: [granted: Permission[]] }>();

type Row = { kind: CanvasAccessKind; perm: Permission; scoped: boolean } | { kind: null; perm: Permission; scoped: false };

const rows = computed<Row[]>(() =>
  PermissionSchema.options
    .filter((p) => props.requested.includes(p))
    .map((p) => {
      const m = /^canvas\.(read|write)\.(all|own)$/.exec(p);
      return m ? { kind: m[1] as CanvasAccessKind, perm: p, scoped: m[2] === 'all' } : { kind: null, perm: p, scoped: false };
    }),
);

function level(kind: CanvasAccessKind): CanvasAccess {
  return props.granted.includes(`canvas.${kind}.all`) ? 'all' : props.granted.includes(`canvas.${kind}.own`) ? 'own' : 'none';
}

function setLevel(kind: CanvasAccessKind, to: CanvasAccess): void {
  const rest = props.granted.filter((p) => p !== `canvas.${kind}.all` && p !== `canvas.${kind}.own`);
  emit('update', to === 'none' ? rest : [...rest, `canvas.${kind}.${to}`]);
}

function setOn(perm: Permission, on: boolean): void {
  emit('update', on ? [...new Set([...props.granted, perm])] : props.granted.filter((p) => p !== perm));
}

const label = (r: Row) => (r.kind ? t('modules.perm.canvas.' + r.kind) : t('modules.perm.' + r.perm));
const hint = (r: Row) => (r.kind ? t(`modules.permDesc.canvas.${r.kind}.${r.scoped ? (level(r.kind) === 'none' ? 'all' : level(r.kind)) : 'own'}`) : t('modules.permDesc.' + r.perm));
</script>

<template>
  <div class="perm-list">
    <div v-for="r in rows" :key="r.perm" class="item" :data-perm="r.kind ? 'canvas.' + r.kind : r.perm">
      <div class="text">
        <span class="label">{{ label(r) }}</span>
        <span class="hint">{{ hint(r) }}</span>
      </div>
      <div v-if="r.kind && r.scoped" class="seg-pills" role="radiogroup" :aria-label="label(r)">
        <button
          v-for="lv in ['all', 'own', 'none'] as const"
          :key="lv"
          type="button"
          role="radio"
          :data-level="lv"
          :aria-checked="level(r.kind) === lv"
          :class="{ active: level(r.kind) === lv }"
          :disabled="disabled"
          @click="setLevel(r.kind, lv)"
        >
          {{ t('modules.scope.' + lv) }}
        </button>
      </div>
      <Toggle v-else :model-value="granted.includes(r.perm)" :label="label(r)" :disabled="disabled" @update:model-value="(v) => setOn(r.perm, v)" />
    </div>
    <!-- API 4.10: what the module's profile cards use of the platform (they have no scripts of their own) -->
    <div v-if="cardUses?.length" class="item uses" data-card-uses>
      <div class="text">
        <span class="label">{{ t('modules.cardUses') }}</span>
        <span class="hint">{{ cardUses.map((u) => t('modules.use.' + u)).join(' · ') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.item {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 54px;
  padding: 8px 0;
  border-top: 1px solid var(--border);
}
.item:first-child {
  border-top: none;
}
.text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.label {
  font-size: 13.5px;
}
.hint {
  font-size: 12px;
  color: var(--text-3);
}
.seg-pills {
  flex: none;
}
</style>
